import React, { forwardRef, FocusEvent } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { GiftOutlined } from '@ant-design/icons';
import { FormInput, FormButton } from '../../../ui';
import './discount-form.scss';

const baseClass = 'discount-form';

interface IDiscountFormData {
  discount: string;
}

interface IDiscountFormProps {
  isLoading?: boolean;
  onSubmit: (values: IDiscountFormData) => void;
  onCancel?: () => void;
}

const validationScheme = yup.object({
  discount: yup.string().required()
}).required();

export const DiscountForm = forwardRef<HTMLFormElement, IDiscountFormProps>(({ onSubmit, onCancel, isLoading = false }, ref): JSX.Element => {
  const form = useForm<IDiscountFormData>({
    mode: 'onBlur',
    defaultValues: {
      discount: ''
    },
    resolver: yupResolver(validationScheme),
  });
  const { control, setValue, handleSubmit, formState: { errors } } = form;

  return (
    <form ref={ref} className={baseClass} onSubmit={handleSubmit(onSubmit)}>
      <div className={`${baseClass}__icon`}>
        <GiftOutlined />
      </div>
      <h3 className={`${baseClass}__title`}>Add Promo Code</h3>
      <p className={`${baseClass}__description`}>
        Apply a promo code to get a discount on your subscription
      </p>
      <Controller
        control={control}
        name="discount"
        render={({ field: { name, onBlur, value, ref } }) => (
          <FormInput
            ref={ref}
            placeholder="Enter promo code"
            error={errors?.discount?.message}
            name={name}
            value={value}
            disabled={isLoading}
            onBlur={onBlur}
            onChange={(e: FocusEvent<HTMLInputElement>) => setValue('discount', e?.target?.value, { shouldDirty: true, shouldValidate: true })}
          />
        )}
      />
      <div className={`${baseClass}__actions`}>
        <FormButton
          theme="grey"
          disabled={isLoading}
          onClick={onCancel}
        >
          Cancel
        </FormButton>
        <FormButton
          theme="green-light"
          loading={isLoading}
          disabled={isLoading}
          htmlType="submit"
        >
          Apply Code
        </FormButton>
      </div>
    </form>
  );
});

DiscountForm.propTypes = {
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func
};